import { RecordType, SupportedCountryAbbreviation } from '@xbcb/shared-types';
import { WorkOrderType, WorkOrderBadge } from '@xbcb/work-order-types';
import CustomsEntrySecondaryHeader from 'components/CustomsEntrySecondaryHeader';
import { CustomsDeclaration } from '../../../loadableComponents';
import {
  getSharedCustomsEntrySubmitButtons,
  sharedCustomsEntryKebabMenuItems,
  appRecordCreateCbamMissingIdTaskMenuItem,
} from '../../utils';
import { WorkOrderRoute } from '../.';
import { sendDocumentsToBroker } from './kebabMenuItems';
import { shouldBlockCurrencySave } from './currencyValidation';
import { getCurrencyForCountryCode } from 'libs/currency/getCurrencyForCountryCode';
import {
  queryCustomsDeclaration,
  updateCustomsDeclaration,
} from './customsDeclaration.query';
import { transformCustomsDeclaration } from '../../../../libs/formTransforms/workOrder/customsDeclaration';
import { isCuDeEditingEnabled, isCbamDetectionEnabled } from 'featureFlags';
import { getCustomsDeclarationDraftEditMode } from 'libs/getCustomsDeclarationDraftEditMode';
import { getAllGoodsShipmentSnapshots } from 'libs/formCore/transforms/goodsShipmentFormRef';
import { getEditedGoodsItemsForShipment } from 'libs/formCore/transforms/goodsItemFormRef';
import { goodsItemDetailSaveSchema } from 'libs/formCore/schemas/forms/goodsItemDetail';
import { goodsShipmentSaveSchema } from 'libs/formCore/schemas/forms/goodsShipment';
import { GoodsItemDetailTransformer } from 'libs/formCore/transforms/GoodsItemDetailTransformer';
import {
  validationErrorStore,
  itemErrorKey,
  shipmentErrorKey,
} from 'libs/formCore/validation';

const customsDeclarationRoute: WorkOrderRoute = {
  Page: CustomsDeclaration,
  recordType: RecordType.CUSTOMS_DECLARATION,
  queries: { get: queryCustomsDeclaration, update: updateCustomsDeclaration },
  SecondaryHeaderContents: [CustomsEntrySecondaryHeader],
  transformUpdateRecordInput: transformCustomsDeclaration.toSchema,
  defaultEditMode: isCuDeEditingEnabled,
  // Draft declarations open directly in edit mode (no "Edit" click), mirroring
  // USCE. Gated to CuDe-editable stages and actively-workable statuses; the
  // Edit button (defaultEditMode) remains for terminal-status entries.
  draftEditMode: ({ existingRecord }) =>
    getCustomsDeclarationDraftEditMode({
      status: existingRecord?.status,
      isEditableStage: isCuDeEditingEnabled,
    }),
  validateUpdate: async () => {
    const errors: { title: string; messages: string[] }[] = [];
    validationErrorStore.clearAll();

    const snapshots = getAllGoodsShipmentSnapshots();
    const itemTransformer = new GoodsItemDetailTransformer();

    for (const [idx, snapshot] of snapshots) {
      // Validate shipment fields
      const shipmentResult = goodsShipmentSaveSchema.safeParse(
        snapshot.formData,
      );
      if (!shipmentResult.success) {
        for (const issue of shipmentResult.error.issues) {
          validationErrorStore.set(
            shipmentErrorKey(idx, issue.path.join('.')),
            { message: issue.message, source: 'schema' },
          );
        }
        errors.push({
          title: `Goods Shipment ${idx + 1}`,
          messages: shipmentResult.error.issues.map((i) => i.message),
        });
      }

      // Validate goods items for this shipment
      const editedItems = getEditedGoodsItemsForShipment(idx);
      const allItems = snapshot.original.goodsItem ?? [];

      for (const item of allItems) {
        const sequence = item.sequence!;
        const editedEntry = editedItems.get(sequence);

        // Use edited form data if visited, otherwise round-trip through transformer
        const formData = editedEntry
          ? editedEntry.formData
          : itemTransformer.toForm(item);

        const result = goodsItemDetailSaveSchema.safeParse(formData);
        if (!result.success) {
          for (const issue of result.error.issues) {
            validationErrorStore.set(
              itemErrorKey(idx, sequence, issue.path.join('.')),
              { message: issue.message, source: 'schema' },
            );
          }
          errors.push({
            title: `Goods Item ${sequence} (Shipment ${idx + 1})`,
            messages: result.error.issues.map((i) => i.message),
          });
        }

        // Check for adjustment codes that conflict with shipment-level selections
        const shipmentCodes = new Set(
          (snapshot.formData.adjustments ?? [])
            .map((a) => a.type)
            .filter(Boolean) as string[],
        );
        if (shipmentCodes.size > 0) {
          const conflictMessages: string[] = [];
          (formData.valuationAdjustments ?? []).forEach((a, i) => {
            if (a.type && shipmentCodes.has(a.type)) {
              const msg = `${a.type} is already applied at shipment level`;
              validationErrorStore.set(
                itemErrorKey(idx, sequence, `valuationAdjustments.${i}.type`),
                { message: msg, source: 'conflict' },
              );
              conflictMessages.push(msg);
            }
          });
          if (conflictMessages.length) {
            errors.push({
              title: `Goods Item ${sequence} (Shipment ${idx + 1})`,
              messages: conflictMessages,
            });
          }
        }
      }

      // Currency is persisted onto the first goods item (see
      // CuDeGoodsShipmentTransformer); if it can't be persisted it reverts to
      // the destination-country default on reload. Block the save only when a
      // NON-default currency would be lost (first item has no cost), so a chosen
      // currency is not silently dropped. Default currency and empty shipments
      // are not blocked.
      const firstItem = allItems[0];
      const firstItemTotalValue = firstItem
        ? (
            editedItems.get(firstItem.sequence!)?.formData ??
            itemTransformer.toForm(firstItem)
          ).totalValue
        : undefined;
      const countryDefaultCurrency = snapshot.formData.importCountry
        ? getCurrencyForCountryCode(
            snapshot.formData
              .importCountry as unknown as SupportedCountryAbbreviation,
          )
        : undefined;
      if (
        shouldBlockCurrencySave(
          snapshot.formData.currency,
          countryDefaultCurrency,
          firstItemTotalValue,
        )
      ) {
        const msg = 'The first product must have a cost to select a currency.';
        validationErrorStore.set(shipmentErrorKey(idx, 'currency'), {
          message: msg,
          source: 'conflict',
        });
        errors.push({
          title: `Goods Shipment ${idx + 1}`,
          messages: [msg],
        });
      }
    }

    return errors.length ? { additionalErrors: errors } : {};
  },
  kebabMenuItems: [
    ...sharedCustomsEntryKebabMenuItems,
    sendDocumentsToBroker,
    appRecordCreateCbamMissingIdTaskMenuItem,
  ],
  submitButtons: getSharedCustomsEntrySubmitButtons(
    WorkOrderType.CustomsDeclaration,
  ),
  // Show the CBAM Declarant ID in the Work Order Info panel for CBAM-badged
  // CuDes; "Not assigned" until an ID is resolved.
  getAdditionalWorkOrderInfo: ({ record }) => {
    if (
      !isCbamDetectionEnabled ||
      !record?.badges?.includes(WorkOrderBadge.CBAM)
    ) {
      return [];
    }
    return [
      {
        label: 'CBAM Declarant ID',
        value: record?.cbam?.declarantId || 'Not assigned',
      },
    ];
  },
};

export default customsDeclarationRoute;
